import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';
import { FiDollarSign, FiAlertCircle } from 'react-icons/fi';
import { fetchProjects, fetchRuns } from '../services/api';

const CostAnalytics = () => {
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  const loadCostData = useCallback(async () => {
    try {
      const projectsResponse = await fetchProjects();
      const runsResponse = await fetchRuns();
      
      // Handle both cases: when fetchProjects/fetchRuns return arrays directly or objects with properties
      const projects = Array.isArray(projectsResponse) ? projectsResponse : (projectsResponse?.projects || []);
      const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);
      
      // Debug: Log the raw data
      console.log('Cost Analytics - Projects Response:', projectsResponse);
      console.log('Cost Analytics - Runs Response:', runsResponse);

      // Calculate total cost using the same logic as Project Details page
      let totalCost = 0;
      
      // Method 1: Try to sum up project.total_cost (already calculated by backend)
      const projectTotalCosts = projects.reduce((sum, project) => {
        console.log(`Project ${project.name}: total_cost = ${project.total_cost}`);
        return sum + (project.total_cost || 0);
      }, 0);
      
      // Method 2: Calculate from project.costs object
      const projectCosts = projects.reduce((sum, project) => {
        const projectCost = Object.values(project.costs || {}).reduce((a, b) => a + b, 0);
        console.log(`Project ${project.name}: costs object = ${JSON.stringify(project.costs)}, sum = ${projectCost}`);
        return sum + projectCost;
      }, 0);
      
      // Method 3: Calculate from runs
      const runsCost = runs.reduce((sum, run) => {
        const runCost = run.llm_calls?.reduce((callSum, call) => {
          if (call.cost > 0) {
            console.log(`Run ${run.run_id}: LLM call cost = ${call.cost}`);
          }
          return callSum + (call.cost || 0);
        }, 0) || 0;
        return sum + runCost;
      }, 0);
      
      console.log('Method 1 - Project total_cost sum:', projectTotalCosts);
      console.log('Method 2 - Project costs object sum:', projectCosts);
      console.log('Method 3 - Runs calculation:', runsCost);
      
      // Force calculation from runs if no project costs are available
      // This ensures we get cost data even if backend hasn't calculated it
      if (runsCost > 0) {
        totalCost = runsCost;
        console.log('Using runs calculation:', runsCost);
      } else if (projectTotalCosts > 0) {
        totalCost = projectTotalCosts;
        console.log('Using project.total_cost:', projectTotalCosts);
      } else if (projectCosts > 0) {
        totalCost = projectCosts;
        console.log('Using project.costs object:', projectCosts);
      } else {
        // If all methods return 0, check if there's any cost data at all
        console.log('WARNING: No cost data found in any method!');
        // Try one more time with a deep scan of all data
        let deepScanCost = 0;
        runs.forEach(run => {
          if (run.cost) deepScanCost += run.cost;
          run.llm_calls?.forEach(call => {
            if (call.cost) deepScanCost += call.cost;
          });
        });
        console.log('Deep scan found cost:', deepScanCost);
        totalCost = deepScanCost;
      }
      
      console.log('Final Total Cost:', totalCost);
      
      // Cost by model - calculate from runs instead of project.costs
      const costByModel = {};
      runs.forEach(run => {
        if (run.llm_calls && run.llm_calls.length > 0) {
          run.llm_calls.forEach(call => {
            const model = call.model || 'Unknown';
            costByModel[model] = (costByModel[model] || 0) + (call.cost || 0);
          });
        }
      });

      // Cost trends over time
      const costTrends = calculateCostTrends(runs, timeRange);
      
      // Cost per token ratio - calculate from runs
      const totalTokens = runs.reduce((sum, run) => {
        const runTokens = run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0;
        return sum + runTokens;
      }, 0);
      const costPerToken = totalTokens > 0 ? (totalCost / totalTokens * 1000).toFixed(4) : 0;

      // Top expensive queries - aggregate by unique prompt
      const queryMap = new Map();
      
      runs
        .filter(run => run.llm_calls && run.llm_calls[0]?.cost)
        .forEach(run => {
          const prompt = run.llm_calls[0].prompt || 'N/A';
          const model = run.llm_calls[0].model || 'unknown';
          const key = `${prompt}:::${model}`; // Unique key for prompt+model combination
          
          if (queryMap.has(key)) {
            const existing = queryMap.get(key);
            existing.cost += run.llm_calls[0].cost;
            existing.tokens += run.llm_calls[0].total_tokens;
            existing.count += 1;
          } else {
            queryMap.set(key, {
              prompt,
              cost: run.llm_calls[0].cost,
              tokens: run.llm_calls[0].total_tokens,
              model,
              count: 1
            });
          }
        });
      
      // Convert to array and sort by total cost
      const expensiveQueries = Array.from(queryMap.values())
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 5)
        .map(query => ({
          ...query,
          avgCost: query.cost / query.count
        }));

      setCostData({
        totalCost,
        costByModel: Object.entries(costByModel).map(([name, value]) => ({ name, value })),
        costTrends,
        costPerToken,
        expensiveQueries,
        totalTokens
      });
    } catch (error) {
      console.error('Error loading cost data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadCostData();
  }, [timeRange, loadCostData]);

  const calculateCostTrends = (runs, range) => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const trends = [];
    const now = new Date();
    
    console.log(`Calculating cost trends for ${days} days`);
          console.log('Run dates:', runs.slice(0, 3).map(r => r.start_time));

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const runsForDay = runs.filter(run => run.start_time && run.start_time.startsWith(dateStr));
      
      const dayCost = runsForDay.reduce((sum, run) => {
        // Calculate cost from llm_calls
        const runCost = run.llm_calls?.reduce((callSum, call) => callSum + (call.cost || 0), 0) || 0;
        return sum + runCost;
      }, 0);
      
      if (runsForDay.length > 0) {
        console.log(`Date ${dateStr}: ${runsForDay.length} runs, total cost: ${dayCost}`);
      }
      
      trends.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cost: dayCost
      });
    }
    
    console.log('Cost trends calculated:', trends);
    return trends;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Don't render if no cost data
  if (!loading && (!costData || costData.totalCost === 0)) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiDollarSign className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Cost Data Available</h3>
          <p className="text-gray-500">Cost analytics will appear here once cost data is available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <FiDollarSign className="w-8 h-8 text-green-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cost Analytics</h2>
              <p className="text-gray-600">Monitor your API usage costs</p>
            </div>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Cost</p>
            <p className="text-2xl font-bold text-gray-900">
              ${costData?.totalCost ? costData.totalCost.toFixed(6) : '0.000000'}
              {(!costData?.totalCost || costData.totalCost === 0) && (
                <span className="text-xs text-yellow-600 block mt-1">
                  Check console for debugging info
                </span>
              )}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Cost per 1K Tokens</p>
            <p className="text-2xl font-bold text-gray-900">${costData?.costPerToken || '0.0000'}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Tokens</p>
            <p className="text-2xl font-bold text-gray-900">
              {costData?.totalTokens?.toLocaleString() || '0'}
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Active Models</p>
            <p className="text-2xl font-bold text-gray-900">
              {costData?.costByModel?.length || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Model Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Cost Distribution by Model</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costData?.costByModel || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {costData?.costByModel?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toFixed(4)}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Trends Line Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Cost Trends</h3>
          {costData?.costTrends && costData.costTrends.some(trend => trend.cost > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costData.costTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis 
                  tickFormatter={(value) => value > 0 ? `$${value.toFixed(6)}` : '$0'} 
                  domain={[0, 'dataMax + 0.000001']}
                />
                <Tooltip formatter={(value) => `$${value.toFixed(8)}`} />
                <Line 
                  type="monotone" 
                  dataKey="cost" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ fill: '#8884d8' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-2">No cost data available for the selected time range</p>
                <p className="text-sm text-gray-400">Cost trends will appear when runs have associated costs</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Expensive Queries */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FiAlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
          Top Expensive Queries
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costData?.expensiveQueries?.map((query, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="truncate max-w-xs" title={query.prompt}>
                      {query.prompt}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.tokens.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${query.cost.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CostAnalytics; 